import React from 'react';
import { Mail, Phone, Instagram, Linkedin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/mockData';

const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language].footer;
  const nav = translations[language].nav;

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <footer className="bg-gradient-to-b from-[#0a0c0f] to-[#050608] text-white py-16 border-t border-[#2a3441] relative">
      {/* Top decorative line with gold accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent"></div>
      
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <h3 className="text-2xl font-bold text-gold-metallic mb-4">PrimeDigital</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              {t.tagline}
            </p>
            <div className="flex space-x-3">
              <a
                href="https://instagram.com/primedigital.es"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-[#1a1d23] hover:bg-gradient-to-r hover:from-[#D4AF37] hover:to-[#F4C542] rounded-lg transition-all duration-300 hover:scale-110 group"
              >
                <Instagram className="w-5 h-5 text-gray-400 group-hover:text-[#141619]" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold text-white mb-4 border-b border-[#2a3441] pb-2">
              {language === 'en' ? 'Quick Links' : language === 'es' ? 'Enlaces Rápidos' : 'Schnelllinks'}
            </h4>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => scrollToSection('services')}
                  className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors duration-300 hover:translate-x-1 inline-block"
                >
                  → {nav.services}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('portfolio')}
                  className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors duration-300 hover:translate-x-1 inline-block"
                >
                  → {nav.portfolio}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors duration-300 hover:translate-x-1 inline-block"
                >
                  → {nav.about}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('testimonials')}
                  className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors duration-300 hover:translate-x-1 inline-block"
                >
                  → {nav.testimonials}
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold text-white mb-4 border-b border-[#2a3441] pb-2">
              {nav.services}
            </h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li className="hover:text-[#D4AF37] transition-colors cursor-pointer">{language === 'en' ? 'Web Design' : language === 'es' ? 'Diseño Web' : 'Webdesign'}</li>
              <li className="hover:text-[#D4AF37] transition-colors cursor-pointer">{language === 'en' ? 'E-commerce' : language === 'es' ? 'E-commerce' : 'E-Commerce'}</li>
              <li className="hover:text-[#D4AF37] transition-colors cursor-pointer">{language === 'en' ? 'SEO' : language === 'es' ? 'SEO' : 'SEO'}</li>
              <li className="hover:text-[#D4AF37] transition-colors cursor-pointer">{language === 'en' ? 'Digital Marketing' : language === 'es' ? 'Marketing Digital' : 'Digitales Marketing'}</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold text-white mb-4 border-b border-[#2a3441] pb-2">
              {nav.contact}
            </h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center space-x-2 text-gray-400 hover:text-[#D4AF37] transition-colors">
                <Phone className="w-4 h-4" />
                <a href="tel:+34633368186">
                  +34-633368186
                </a>
              </li>
              <li className="flex items-center space-x-2 text-gray-400 hover:text-[#D4AF37] transition-colors">
                <Mail className="w-4 h-4" />
                <a href="mailto:Info@primedigital.es" className="break-all">
                  Info@primedigital.es
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-[#2a3441]">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} <span className="text-[#D4AF37] font-semibold">PrimeDigital</span>. {t.rights}.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
              <button className="hover:text-[#D4AF37] transition-colors duration-300">
                {language === 'en' ? 'Privacy Policy' : language === 'es' ? 'Política de Privacidad' : 'Datenschutz'}
              </button>
              <button className="hover:text-[#D4AF37] transition-colors duration-300">
                {language === 'en' ? 'Terms of Service' : language === 'es' ? 'Términos de Servicio' : 'Nutzungsbedingungen'}
              </button>
            </div>
          </div>
          
          {/* Legal Entity Notice */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              PrimeDigital {language === 'en' ? 'is operating under' : language === 'es' ? 'opera bajo' : 'wird betrieben von'} <span className="text-gray-400">Mallorca Prime SL</span>
            </p>
          </div>
          
          {/* Made with tag */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              {language === 'en' ? 'Built with' : language === 'es' ? 'Construido con' : 'Erstellt mit'} <span className="text-[#D4AF37]">♥</span> {language === 'en' ? 'and AI technology' : language === 'es' ? 'y tecnología IA' : 'und KI-Technologie'}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
